import {
  formatThaiMonth,
  formatEnglishMonth,
  formatThaiYear,
  formatTimeRange,
  isSameDay,
  isToday
} from 'react-lib/utils/dateUtils';

// Types
export interface AppointmentMeHey {
  id?: number;
  doctor_dsb_id?: number;
  provider_id?: number;
  ishaveAppointment?: boolean;
  availableSlots?: number | null;
  title?: string;
  startTime?: Date;
  endTime?: Date;
  location?: string;
  doctor?: string;
  order_note?: string;
  tel?: string;
  status?: number;
  estimated_at?: string;
  patientID?: string | null;
  patientName?: string;
  phoneNumber?: string;
}

export type ViewType = 'month' | 'week' | 'day';

export interface CalendarState {
  currentDate: Date;
  viewType: ViewType;
  selectedDate: Date | null;
}

// Re-export utility functions from dateUtils for backward compatibility
export {
  formatThaiMonth,
  formatEnglishMonth,
  formatThaiYear,
  formatTimeRange,
  isSameDay,
  isToday
};

export const getMonthDays = (year: number, month: number): Date[] => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const days: Date[] = [];

  // Add days from previous month to fill the first week
  // getDay() returns 0 (Sun) to 6 (Sat), but we want Monday (1) to be first day
  const firstDayOfWeek = firstDay.getDay();
  const daysToSubtract = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1; // Convert to Monday-based week
  const startDate = new Date(firstDay);
  startDate.setDate(startDate.getDate() - daysToSubtract);

  // Generate days until we've covered all weeks containing current month days
  let currentDate = new Date(startDate);
  let weeksAdded = 0;

  while (weeksAdded < 6) {
    let weekDays: Date[] = [];
    let hasCurrentMonthDay = false;

    // Generate one week (7 days)
    for (let i = 0; i < 7; i++) {
      const day = new Date(currentDate);
      weekDays.push(day);

      // Check if this day is in the current month
      if (day.getMonth() === month && day.getFullYear() === year) {
        hasCurrentMonthDay = true;
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Only add this week if it contains at least one day from current month
    if (hasCurrentMonthDay) {
      days.push(...weekDays);
      weeksAdded++;
    } else if (days.length > 0) {
      // If we already have days and this week has no current month days, we're done
      break;
    }
  }

  return days;
};

export const getWeekDays = (date: Date): Date[] => {
  const days: Date[] = [];
  const startOfWeek = new Date(date);
  // getDay() returns 0 (Sun) to 6 (Sat), but we want Monday as first day
  const dayOfWeek = date.getDay();
  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert to Monday-based week
  startOfWeek.setDate(date.getDate() - daysToSubtract);

  for (let i = 0; i < 7; i++) {
    const day = new Date(startOfWeek);
    day.setDate(day.getDate() + i);
    days.push(day);
  }

  return days;
};

export const getDayAppointments = (appointments: AppointmentMeHey[], day: Date): AppointmentMeHey[] => {
  if (day === undefined) {
    console.warn('getDayAppointments called with undefined day');
    return [];
  }

  return appointments.filter(apt =>  isSameDay(apt.startTime, day) );
};

export const getDayOfWeekThai = (dayIndex: number): string => {
  const days = ['อา.', 'จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.'];
  return days[dayIndex];
};

export const getDayOfWeekThaiLong = (dayIndex: number): string => {
  const days = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์'];
  return days[dayIndex];
};